import { useCallback } from 'react';
import { getAnalyticsBuilderAutocompleteData } from '@/api/services-data';
import Autocomplete from '@/components/formControl/Autocomplete';
import Dropdown, { type DropdownOption } from '@/components/formControl/Dropdown';
import FilterableDropdownTree, {
  type FilterableDropdownTreeOption,
} from '@/components/formControl/FilterableDropdownTree';
import type {
  AnalyticsBuilderFieldOption,
  AnalyticsBuilderFieldType,
  AnalyticsBuilderResolvedTemplateType,
} from '@/types';
import { AnalyticsBuilderAutocompleteTaxonomy, AnalyticsBuilderFieldTypeType } from '@/types';
import classNames from 'classnames';

type AnalyticsBuilderSetupFieldProps = {
  field: AnalyticsBuilderFieldType;
  onChange: (fieldName: string, value: string, optionValue?: string) => void;
  onShakeEnd: (fieldName: string) => void;
  errorMessage?: string;
  shakeField: boolean;
  showError: boolean;
  template: AnalyticsBuilderResolvedTemplateType;
  value: string;
};

const inputLabelConfigs = [
  {
    urlSegement: 'marketentryenvironment-industryId',
    label: 'Industry',
    name: 'Industry',
  },
  {
    urlSegement: 'marketentryenvironment-geographyId',
    label: 'Geography',
    name: 'Geography',
  },
  {
    urlSegement: 'fiveforcesanalysis-industryId',
    label: 'Industry',
    name: 'Industry',
  },
  {
    urlSegement: 'fiveforcesanalysis-geographyId',
    label: 'Geography',
    name: 'Geography',
  },
  {
    urlSegement: 'technologyimpact-industryId',
    label: 'Industry',
    name: 'Industry',
  },
  {
    urlSegement: 'technologyimpact-geographyId',
    label: 'Geography',
    name: 'Geography',
  },
  {
    urlSegement: 'supplychainstrategy-industryId',
    label: 'Industry',
    name: 'Industry',
  },
  {
    urlSegement: 'supplychainstrategy-geographyId',
    label: 'Geography',
    name: 'Geography',
  },
  {
    urlSegement: 'innovationlandscape-searchText',
    label: 'Describe the innovation landscape you want to explore eg ‘Technological innovations in EVs in China’',
    name: 'Innovation landscape',
  },
  {
    urlSegement: 'cityprofilepestanalysis-cityName',
    label: 'Enter a city name eg. “London”',
    name: 'City',
  },
  {
    urlSegement: 'companyrdinnovationinsights-companyName',
    label: 'Enter a company name eg. “Tesla”',
    name: 'Company',
  },
  {
    urlSegement: 'countrymacroeconomicdata-geographyId',
    label: 'Geography',
    name: 'Geography',
  },
];

const getFieldLabel = (
  field: AnalyticsBuilderFieldType,
  template?: AnalyticsBuilderResolvedTemplateType | null,
  label?: boolean,
) => {
  const inputLabelConfig = inputLabelConfigs.find(
    (input) => input.urlSegement === template?.urlSegement + '-' + field.queryParameterName,
  );
  if (inputLabelConfig) {
    return label ? inputLabelConfig.label : inputLabelConfig.name;
  }

  return field.displayName || field.queryParameterName;
};

const mapFieldOptionsToFilterableDropdownTreeOptions = (
  options: AnalyticsBuilderFieldOption[],
): FilterableDropdownTreeOption[] =>
  options.map((option) => ({
    label: option.name,
    value: option.id,
    options: option.options ? mapFieldOptionsToFilterableDropdownTreeOptions(option.options) : undefined,
    disabled: option.disabled,
  }));

const mapFieldOptionsToDropdownOptions = (options: AnalyticsBuilderFieldOption[]): DropdownOption[] =>
  options.flatMap((option) => [
    {
      label: option.name,
      value: option.key || option.id,
    },
    ...mapFieldOptionsToDropdownOptions(option.options || []),
  ]);

const getAutocompleteTaxonomy = (field: AnalyticsBuilderFieldType): AnalyticsBuilderAutocompleteTaxonomy => {
  if (field.type === AnalyticsBuilderFieldTypeType.AutocompleteCity) {
    return AnalyticsBuilderAutocompleteTaxonomy.City;
  }
  if (field.type === AnalyticsBuilderFieldTypeType.AutocompleteGeography) {
    return AnalyticsBuilderAutocompleteTaxonomy.Geograph;
  }
  if (field.type === AnalyticsBuilderFieldTypeType.AutocompleteCompany) {
    return AnalyticsBuilderAutocompleteTaxonomy.Company;
  }
  return AnalyticsBuilderAutocompleteTaxonomy.Industry;
};

const autocompleteFieldTypes = [
  AnalyticsBuilderFieldTypeType.AutocompleteCity,
  AnalyticsBuilderFieldTypeType.AutocompleteGeography,
  AnalyticsBuilderFieldTypeType.AutocompleteCompany,
];

const AnalyticsBuilderSetupField = ({
  field,
  onChange,
  onShakeEnd,
  errorMessage,
  shakeField,
  showError,
  template,
  value,
}: AnalyticsBuilderSetupFieldProps) => {
  const errorId = `analytics-builder-${field.queryParameterName}-error`;
  const loadAutocompleteOptions = useCallback(
    (searchTerm: string) => getAnalyticsBuilderAutocompleteData(getAutocompleteTaxonomy(field), searchTerm),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [field.queryParameterName],
  );

  if (field.type === AnalyticsBuilderFieldTypeType.Hidden) {
    return null;
  }

  const defaultErrorMessage = `Please provide ${getFieldLabel(field, template).toLowerCase()} to generate this report.`;

  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-zinc-800 first-letter:uppercase">
        {getFieldLabel(field, template, true)}
      </span>
      {field.type === AnalyticsBuilderFieldTypeType.TaxonomyDropdown ? (
        <div
          onAnimationEnd={() => onShakeEnd(field.queryParameterName)}
          className={classNames({ 'animate-field-shake': shakeField })}
        >
          <FilterableDropdownTree
            name={field.queryParameterName}
            options={mapFieldOptionsToFilterableDropdownTreeOptions(field.options || [])}
            value={value}
            placeholder={`Select ${getFieldLabel(field, template)}`}
            invalid={showError}
            onChange={(e) => {
              onChange(field.queryParameterName, e.target.value || '', e.target.label || '');
            }}
          />
        </div>
      ) : field.type === AnalyticsBuilderFieldTypeType.Dropdown ? (
        <div
          onAnimationEnd={() => onShakeEnd(field.queryParameterName)}
          className={classNames({ 'animate-field-shake': shakeField })}
        >
          <Dropdown
            name={field.queryParameterName}
            options={mapFieldOptionsToDropdownOptions(field.options || [])}
            value={value || undefined}
            placeholder={`Select ${getFieldLabel(field, template)}`}
            onChange={(e) => {
              onChange(field.queryParameterName, e.target.value || '');
            }}
            className={classNames(
              'w-full',
              '[&_.main-button]:h-11 [&_.main-button]:rounded-[6px] [&_.main-button]:border [&_.main-button]:bg-white [&_.main-button]:px-3 [&_.main-button]:text-sm [&_.main-button]:text-zinc-800',
              '[&_.content]:!w-full [&_ul]:!w-full',
              '[&_.popover-title]:truncate',
              '[&_.content-wrapper]:pr-2 [&_.content-wrapper]:pl-1',
              showError ? '[&_.main-button]:border-red-500' : '[&_.main-button]:border-zinc-200',
            )}
            contentWrapperClassName="!w-[min(372px,calc(100vw-80px))] max-h-80"
            offset={4}
          />
        </div>
      ) : autocompleteFieldTypes.includes(field.type) ? (
        <div
          onAnimationEnd={() => onShakeEnd(field.queryParameterName)}
          className={classNames({ 'animate-field-shake': shakeField })}
        >
          <Autocomplete
            name={field.queryParameterName}
            value={value}
            placeholder={`Enter ${getFieldLabel(field, template).toLowerCase()}`}
            invalid={showError}
            loadOptions={loadAutocompleteOptions}
            onChange={(e) => {
              onChange(field.queryParameterName, e.target.label || '', e.target.value || '');
            }}
          />
        </div>
      ) : (
        <input
          onAnimationEnd={() => onShakeEnd(field.queryParameterName)}
          className={classNames(
            'h-11 w-full rounded-[6px] border bg-white px-3 text-sm text-zinc-800 transition outline-none placeholder:text-zinc-400',
            {
              'border-red-500 focus:border-red-500': showError,
              'border-zinc-200 focus:border-zinc-400': !showError,
            },
            { 'animate-field-shake': shakeField },
          )}
          value={value}
          onChange={(e) => {
            onChange(field.queryParameterName, e.target.value);
          }}
          placeholder={`Enter ${getFieldLabel(field, template).toLowerCase()}`}
          required
          aria-invalid={showError}
          aria-describedby={showError ? errorId : undefined}
        />
      )}
      {showError && (
        <p id={errorId} className="mt-2 text-sm font-medium text-red-600">
          {errorMessage || defaultErrorMessage}
        </p>
      )}
    </label>
  );
};

export default AnalyticsBuilderSetupField;
